import React, { useState, useEffect, useMemo } from 'react';
import ActivitiesFilterBar from '../../components/activities/ActivitiesFilterBar';
import ActivitiesMapView from '../../components/activities/ActivitiesMapView';
import ActivitiesList from '../../components/activities/ActivitiesList';
import ActivitiesLoadMoreButton from '../../components/activities/ActivitiesLoadMoreButton';
import { fetchAllActivities } from '../../lib/api';
import { applyFilters } from '../../lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { readSessionJson, writeSessionJson } from '../../utils/storage';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { resolveActivityCategoryFilterLabel } from '../../lib/activityTaxonomy';

const PAGE_SIZE = 24;
const STORAGE_KEY = 'alakai_activities_index_state_v2';

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const toString = (value) => (typeof value === 'string' ? value : '');
const normalizeParam = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const normalizeFilters = (raw) => ({
  region: toArray(raw?.region),
  category: toArray(raw?.category),
  cost: toArray(raw?.cost),
  difficulty: toArray(raw?.difficulty),
  searchText: toString(raw?.searchText),
});

const ActivitiesIndex = () => {
  const location = useLocation();
  const { island, islandConfig } = getRuntimeSiteContext();
  const storageKey = useMemo(() => `${STORAGE_KEY}_${island}`, [island]);
  const stored = useMemo(() => readSessionJson(storageKey), [storageKey]);
  const [all, setAll] = useState([]);
  const defaultFilters = useMemo(
    () => ({
      region: [], category: [], cost: [], difficulty: [], searchText: ''
    }),
    []
  );
  const [filters, setFilters] = useState(() => {
    const normalized = normalizeFilters(stored?.filters);
    return { ...defaultFilters, ...normalized };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [mapBounds, setMapBounds] = useState(null);
  const mapViewRef = React.useRef(stored?.mapView ?? null);

  // Initialize filters from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const regionParam = searchParams.get('region');
    const categoryParam = searchParams.get('category');
    const categoryLabel = categoryParam
      ? resolveActivityCategoryFilterLabel(normalizeParam(categoryParam))
      : '';

    if (!regionParam && !categoryLabel) return;

    setFilters(prev => ({
      ...prev,
      ...(regionParam ? { region: [regionParam] } : {}),
      ...(categoryLabel ? { category: [categoryLabel] } : {}),
    }));
  }, [location.search]);

  // Load all activities once, then shuffle to get a random selection
  useEffect(() => {
    fetchAllActivities().then(data => {
      const shuffled = data.sort(() => 0.5 - Math.random());
      setAll(shuffled);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    writeSessionJson(storageKey, {
      filters,
      mapView: mapViewRef.current,
      storedAt: Date.now(),
    });
  }, [filters, storageKey]);

  const filtered = useMemo(
    () => applyFilters(all, filters),
    [all, filters]
  );

  const inViewport = useMemo(() => {
    if (!mapBounds) return filtered;
    return filtered.filter(a => {
      const { lat, lng } = a.position ?? a.location;
      return mapBounds.contains({ lat, lng });
    });
  }, [filtered, mapBounds]);

  const displayed = useMemo(
    () => inViewport.slice(0, currentPage * PAGE_SIZE),
    [inViewport, currentPage]
  );

  return (
    <div className="activities-index-page pt-0 space-y-6">
      <Helmet>
        <title>{`${islandConfig.title} Activities | Alaka'i Aloha`}</title>
        <meta
          name="description"
          content={`Explore ${islandConfig.title} activities and experiences, with map view, filters, and local tips to plan your trip.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl('/activities')} />
      </Helmet>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-4xl font-bold">Activities</h1>
        <Link
          to="/activities/list"
          className="btn-secondary-compact"
          title="View a complete list of all activities"
        >
          View as list
        </Link>
      </div>

      <ActivitiesFilterBar filters={filters} onChange={setFilters} />

      <div className="mt-6">
        <ActivitiesMapView
          activities={displayed}
          onBoundsChanged={setMapBounds}
          initialCenter={mapViewRef.current?.center}
          initialZoom={mapViewRef.current?.zoom}
          onViewChanged={(nextView) => {
            mapViewRef.current = nextView;
            writeSessionJson(storageKey, {
              filters,
              mapView: nextView,
              storedAt: Date.now(),
            });
          }}
        />
      </div>

      <div className="mt-6">
        <ActivitiesList activities={displayed} />
      </div>

      {displayed.length < inViewport.length && (
        <ActivitiesLoadMoreButton 
          onClick={() => setCurrentPage(p => p + 1)} />
      )}
    </div>
  );
};

export default ActivitiesIndex;
